import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { AuthContext } from '../App';
import axios from 'axios';
import { Search, Star, ExternalLink, ChevronLeft, ChevronRight, X, Globe, BookOpen, GraduationCap, Award, AlertCircle, Loader2, SlidersHorizontal, Filter, Upload, CheckCircle } from 'lucide-react';
import Footer from '../components/Footer';
import AdvisorRibbon from '../components/AdvisorRibbon';

import SEOHead from '../components/SEOHead';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRIES = [
  'Worldwide', 'UK', 'USA', 'Germany', 'Australia', 'Canada', 'Netherlands',
  'Switzerland', 'Japan', 'Singapore', 'South Korea', 'New Zealand', 'Ireland',
  'Sweden', 'France', 'Hong Kong', 'Taiwan', 'Belgium', 'Austria', 'Norway',
  'Denmark', 'Italy', 'Spain', 'Finland', 'Portugal', 'China',
];

const SUBJECTS = [
  { value: '', label: 'All Subjects' },
  { value: 'engineering_it', label: 'Engineering & IT' },
  { value: 'finance_management', label: 'Finance & Management' },
  { value: 'medicine_bio', label: 'Medicine, Dentistry, Pharma & Bio' },
];

const LEVELS = [
  { value: '', label: 'All Levels' },
  { value: 'bachelor', label: 'Bachelor' },
  { value: 'master', label: 'Master' },
  { value: 'phd', label: 'PhD' },
  { value: 'phd_studentship', label: 'PhD Studentship' },
];

const TYPES = [
  { value: '', label: 'All Types' },
  { value: 'fully_funded', label: 'Fully Funded' },
  { value: 'university', label: 'University Scholarship' },
];

const LEVEL_LABELS = {
  bachelor: { label: 'Bachelor', color: 'bg-blue-50 text-blue-700 border-blue-700' },
  master: { label: 'Master', color: 'bg-purple-50 text-purple-700 border-purple-700' },
  phd: { label: 'PhD', color: 'bg-teal-50 text-teal-700 border-teal-700' },
  phd_studentship: { label: 'PhD Studentship', color: 'bg-cyan-50 text-cyan-700 border-cyan-700' },
};

const TYPE_LABELS = {
  fully_funded: { label: 'Fully Funded', color: 'bg-green-50 text-green-700 border-green-700' },
  university: { label: 'University', color: 'bg-amber-50 text-amber-700 border-amber-700' },
};

function ScholarshipCard({ scholarship, onViewDetails }) {
  const levels = scholarship.level || [];
  const types = scholarship.type || [];

  return (
    <div
      data-testid="scholarship-card"
      className={`relative bg-gray-50 rounded-xl border transition-all duration-200 hover:shadow-xl hover:shadow-amber-900/20 hover:-translate-y-0.5 flex flex-col ${
        scholarship.cv_matched ? 'border-amber-500 shadow-amber-900/30 shadow-md' : 'border-gray-300'
      }`}
    >
      {/* CV Match star badge */}
      {scholarship.cv_matched && (
        <div className="absolute -top-2 -right-2 bg-amber-500 rounded-full p-1.5 shadow-lg z-10" title="Matches your CV">
          <Star className="w-3 h-3 text-gray-900 fill-white" />
        </div>
      )}

      <div className="p-4 flex flex-col flex-1">
        {/* Badges — horizontal scroll, no wrap */}
        <div className="flex overflow-x-auto gap-1.5 mb-3 pb-0.5 scrollbar-hide">
          {levels.map(lv => {
            const cfg = LEVEL_LABELS[lv];
            return cfg ? (
              <span key={lv} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap shrink-0 ${cfg.color}`}>
                {cfg.label}
              </span>
            ) : null;
          })}
          {types.map(tp => {
            const cfg = TYPE_LABELS[tp];
            return cfg ? (
              <span key={tp} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border whitespace-nowrap shrink-0 ${cfg.color}`}>
                {cfg.label}
              </span>
            ) : null;
          })}
        </div>

        {/* Title — max 2 lines */}
        <h3 className="text-gray-900 font-bold text-sm leading-snug mb-1 line-clamp-2">
          {scholarship.title}
        </h3>

        {/* University */}
        <p className="text-amber-700 text-xs font-medium mb-1 truncate">
          {scholarship.university}
        </p>

        {/* Country */}
        <p className="text-gray-500 text-xs mb-3 flex items-center gap-1">
          <Globe className="w-3 h-3 flex-shrink-0" />
          {scholarship.country}
        </p>

        {/* Description */}
        <p className="text-gray-600 text-xs leading-relaxed line-clamp-2 flex-1 mb-3">
          {scholarship.description}
        </p>

        {/* Deadline + Funding in two lines, not wrap */}
        <div className="space-y-0.5 mb-4 text-xs">
          {scholarship.deadline && (
            <p className="text-gray-500 truncate">
              <span className="text-gray-600 font-medium">Deadline:</span> {scholarship.deadline}
            </p>
          )}
          {scholarship.funding_amount && (
            <p className="text-gray-500 truncate">
              <span className="text-gray-600 font-medium">Funding:</span> {scholarship.funding_amount}
            </p>
          )}
        </div>

        {/* CTA */}
        <button
          data-testid="view-details-btn"
          onClick={() => onViewDetails(scholarship)}
          className="w-full py-2.5 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 text-white text-sm font-semibold hover:from-amber-500 hover:to-orange-500 transition-all duration-150 active:scale-95"
        >
          View Details
        </button>
      </div>
    </div>
  );
}

function ScholarshipDetailModal({ scholarship, onClose }) {
  if (!scholarship) return null;

  const levels = scholarship.level || [];
  const types = scholarship.type || [];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-gray-900/50 backdrop-blur-sm" />
      <div
        className="relative bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl border border-gray-300 max-h-[92vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Close button — pinned to top-right corner, white, no shadow */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-20 w-7 h-7 flex items-center justify-center text-gray-900 rounded-full hover:bg-gray-100 transition-colors"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-start gap-3 z-10">
          <div className="flex-1 min-w-0 pr-6">
            <div className="flex flex-wrap gap-1.5 mb-2">
              {levels.map(lv => {
                const cfg = LEVEL_LABELS[lv];
                return cfg ? (
                  <span key={lv} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cfg.color}`}>
                    {cfg.label}
                  </span>
                ) : null;
              })}
              {types.map(tp => {
                const cfg = TYPE_LABELS[tp];
                return cfg ? (
                  <span key={tp} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cfg.color}`}>
                    {cfg.label}
                  </span>
                ) : null;
              })}
            </div>
            <h2 className="text-gray-900 font-bold text-base sm:text-lg leading-snug">{scholarship.title}</h2>
            <p className="text-amber-700 text-sm font-medium mt-0.5">{scholarship.university}</p>
            <p className="text-gray-500 text-xs flex items-center gap-1 mt-0.5">
              <Globe className="w-3 h-3" />{scholarship.country}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-5 space-y-5">
          {/* Funding + Deadline quick info */}
          {(scholarship.funding_amount || scholarship.deadline) && (
            <div className="flex flex-wrap gap-3">
              {scholarship.funding_amount && (
                <div className="bg-green-50 border border-green-800 rounded-lg px-3 py-2 flex-1 min-w-[140px]">
                  <p className="text-green-700 text-[10px] font-semibold uppercase tracking-wide mb-0.5">Funding</p>
                  <p className="text-gray-900 text-sm">{scholarship.funding_amount}</p>
                </div>
              )}
              {scholarship.deadline && (
                <div className="bg-amber-50 border border-amber-800 rounded-lg px-3 py-2 flex-1 min-w-[140px]">
                  <p className="text-amber-700 text-[10px] font-semibold uppercase tracking-wide mb-0.5">Deadline</p>
                  <p className="text-gray-900 text-sm">{scholarship.deadline}</p>
                </div>
              )}
            </div>
          )}

          {/* Description */}
          <div>
            <h4 className="text-gray-700 text-xs font-semibold uppercase tracking-widest mb-2 flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5 text-amber-500" /> About
            </h4>
            <p className="text-gray-700 text-sm leading-relaxed">{scholarship.description}</p>
          </div>

          {/* Eligibility */}
          {scholarship.eligibility && (
            <div>
              <h4 className="text-gray-700 text-xs font-semibold uppercase tracking-widest mb-2 flex items-center gap-1.5">
                <Award className="w-3.5 h-3.5 text-amber-500" /> Eligibility
              </h4>
              <p className="text-gray-700 text-sm leading-relaxed">{scholarship.eligibility}</p>
            </div>
          )}

          {/* Application Process */}
          {scholarship.application_process && (
            <div>
              <h4 className="text-gray-700 text-xs font-semibold uppercase tracking-widest mb-2 flex items-center gap-1.5">
                <GraduationCap className="w-3.5 h-3.5 text-amber-500" /> How to Apply
              </h4>
              <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-line">{scholarship.application_process}</p>
            </div>
          )}

          {/* Apply Button */}
          {scholarship.official_link && (
            <a
              href={scholarship.official_link}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="apply-now-btn"
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 text-white font-semibold text-sm hover:from-amber-500 hover:to-orange-500 transition-all duration-150 active:scale-95 mt-2"
            >
              Apply on Official Website <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Scholarships() {
  const { user, setUser } = useContext(AuthContext);

  const [scholarships, setScholarships] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [country, setCountry] = useState('');
  const [level, setLevel] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [subject, setSubject] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('all');
  const [cvKeywords, setCvKeywords] = useState([]);
  const [cvLoading, setCvLoading] = useState(false);
  const [cvUploading, setCvUploading] = useState(false);
  const [hasCv, setHasCv] = useState(false);
  const [cvSource, setCvSource] = useState('');
  const [selectedScholarship, setSelectedScholarship] = useState(null);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [cvSearchStarted, setCvSearchStarted] = useState(false);
  const searchDebounce = useRef(null);
  const cvUploadRef = useRef(null);

  // Mark scholarships as seen (clears notification dot in BottomNav)
  useEffect(() => {
    localStorage.setItem('scholarships_last_seen', new Date().toISOString());
  }, []);

  // Scroll listener — reveal CV upload card after scrolling ~300px
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300 && !hasScrolled) {
        setHasScrolled(true);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasScrolled]);

  // Fetch CV / profile keywords when user is logged in
  useEffect(() => {
    if (!user) { setCvKeywords([]); setHasCv(false); return; }
    setCvLoading(true);
    axios.get(`${API}/scholarships/cv-keywords`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then(res => {
        const kws = res.data?.keywords || [];
        setCvKeywords(kws);
        setHasCv(kws.length > 0);
        setCvSource(res.data?.source || 'cv');
      })
      .catch(() => { setCvKeywords([]); setHasCv(false); })
      .finally(() => setCvLoading(false));
  }, [user]);

  const fetchScholarships = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page };
      if (country && country !== 'Worldwide') params.country = country;
      if (level) params.level = level;
      if (typeFilter) params.type_filter = typeFilter;
      if (subject) params.subject = subject;
      if (searchQuery.trim()) params.search = searchQuery.trim();
      // Pass CV keywords for matching — always so ranked results come first
      if (viewMode === 'matching' && cvKeywords.length) {
        params.cv_keywords = cvKeywords.join(',');
      } else if (viewMode !== 'matching' && cvKeywords.length) {
        // Still pass keywords so cards get cv_matched flag even in 'all' mode
        params.cv_keywords = cvKeywords.join(',');
      }

      const res = await axios.get(`${API}/scholarships`, { params });
      setScholarships(res.data.scholarships || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.total_pages || 1);
    } catch {
      setScholarships([]);
    } finally {
      setLoading(false);
    }
  }, [page, country, level, typeFilter, subject, searchQuery, cvKeywords, viewMode]);

  useEffect(() => {
    fetchScholarships();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [fetchScholarships]);

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [country, level, typeFilter, subject, searchQuery, viewMode]);

  // Debounced search
  const handleSearchChange = (val) => {
    setSearchInput(val);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setSearchQuery(val), 450);
  };

  // CV upload — same endpoint & storage as Profile page and Job apply modal
  const handleCvUpload = async (file) => {
    if (!file) return;
    const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) {
      alert('Please upload a PDF or Word document');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('CV must be under 5 MB');
      return;
    }
    setCvUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${API}/upload-cv`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData,
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.detail || 'Upload failed');

      // Update shared user context — same cv_url used by Profile & Job Apply
      if (setUser && user) {
        setUser({ ...user, profile: { ...user.profile, cv_url: data.url } });
      }

      // Immediately extract keywords and switch to Matching tab
      try {
        const kwRes = await axios.get(`${API}/scholarships/cv-keywords`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const kws = kwRes.data?.keywords || [];
        setCvKeywords(kws);
        setHasCv(kws.length > 0);
        setCvSource(kwRes.data?.source || 'cv');
        if (kws.length > 0) {
          setViewMode('matching');
          // Trigger a new Gemini search specific to these CV keywords
          // Results are appended to DB → they'll appear starred in Matching tab
          if (!cvSearchStarted) {
            setCvSearchStarted(true);
            axios.post(`${API}/scholarships/cv-search`, { keywords: kws }, {
              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            }).catch(() => {}); // fire-and-forget

            // After ~60 s (search estimated completion), clear scholarships_last_seen
            // so BottomNav notification dot lights up when the user navigates away
            setTimeout(() => {
              localStorage.removeItem('scholarships_last_seen');
              window.dispatchEvent(new CustomEvent('scholarships_cv_done'));
            }, 60000);
          }
        }
      } catch {}
    } catch (err) {
      alert(err.message || 'Upload failed. Try again.');
    } finally {
      setCvUploading(false);
      if (cvUploadRef.current) cvUploadRef.current.value = '';
    }
  };

  const handleViewDetails = (scholarship) => {
    if (!user) {
      window.dispatchEvent(new CustomEvent('openAuthModal'));
      return;
    }
    setSelectedScholarship(scholarship);
  };

  const handleFilterReset = () => {
    setCountry('');
    setLevel('');
    setTypeFilter('');
    setSubject('');
    setSearchInput('');
    setSearchQuery('');
  };

  const hasActiveFilters = country || level || typeFilter || subject || searchQuery;

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Global Scholarships 2025 for Indian & International Students"
        description="Discover 450+ fully-funded and partial scholarships for Masters, PhD and Bachelor students worldwide. Filter by country, subject and funding type. Updated daily."
        path="/scholarships"
      />
      <div className="bg-gradient-to-br from-white via-amber-50 to-white border-b border-gray-200 pt-5 pb-6 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-2 mb-1">
            <GraduationCap className="w-4 h-4 text-amber-700" />
            <span className="text-amber-700 text-[11px] font-semibold uppercase tracking-widest">Scholarships</span>
          </div>
          <h1 className="text-gray-900 text-2xl sm:text-3xl font-bold mb-1">Worldwide Scholarships</h1>
          <p className="text-gray-600 text-xs sm:text-sm max-w-xl leading-relaxed">
            Bachelors, MS & PhD scholarships in UK, USA, Canada, Europe, Australia, New Zealand, Japan, Korea and Singapore — verified from official university sources.
          </p>

          {/* CV match status badge — only when already matched */}
          {user && hasCv && cvSource === 'cv' && (
            <div className="mt-3 inline-flex items-center gap-1.5 bg-amber-50 border border-amber-700 rounded-full px-3 py-1 text-amber-700 text-[11px] font-medium">
              <Star className="w-3 h-3 fill-amber-400 text-amber-700" />
              Matching scholarships to your CV
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-5">

        {/* Search bar — always visible */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            data-testid="scholarship-search"
            type="text"
            value={searchInput}
            onChange={e => handleSearchChange(e.target.value)}
            placeholder="Search by keyword, university, country, subject..."
            className="w-full bg-white border border-gray-300 text-gray-900 text-sm rounded-xl pl-9 pr-9 py-2.5 focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-500/20 placeholder-gray-500"
          />
          {searchInput && (
            <button onClick={() => { setSearchInput(''); setSearchQuery(''); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-900">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        {/* CV Upload Card — shown after scroll when user has no CV uploaded yet */}
        {user && !cvLoading && !(hasCv && cvSource === 'cv') && hasScrolled && (
          <div className="mb-4 rounded-xl border border-amber-800/60 bg-gradient-to-r from-amber-950/40 to-orange-950/30 p-3.5 flex items-center gap-3">
            {cvUploading ? (
              <>
                <Loader2 className="w-5 h-5 text-amber-700 animate-spin shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-amber-700 text-sm font-semibold">Analysing your CV...</p>
                  <p className="text-gray-600 text-xs">Extracting keywords to find your best matches</p>
                </div>
              </>
            ) : (
              <>
                <div className="w-9 h-9 rounded-lg bg-amber-600/20 border border-amber-700/50 flex items-center justify-center shrink-0">
                  <Upload className="w-4 h-4 text-amber-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-gray-900 text-sm font-semibold leading-tight">Let our AI search scholarships based on your CV</p>
                  <p className="text-gray-600 text-[11px] mt-0.5">Same CV used in Profile & job applications</p>
                </div>
                <button
                  data-testid="cv-upload-btn"
                  onClick={() => cvUploadRef.current?.click()}
                  className="shrink-0 bg-amber-600 hover:bg-amber-500 active:scale-95 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-all"
                >
                  Upload CV
                </button>
              </>
            )}
            <input
              ref={cvUploadRef}
              type="file"
              accept=".pdf,.doc,.docx"
              className="hidden"
              onChange={e => handleCvUpload(e.target.files?.[0])}
            />
          </div>
        )}

        {/* View Mode Tabs: All / Matching */}
        {user && hasCv && (
          <div className="flex gap-2 mb-4">
            <button
              data-testid="tab-all"
              onClick={() => setViewMode('all')}
              className={`flex-1 text-xs font-semibold py-2 rounded-lg border transition-colors ${viewMode === 'all' ? 'bg-gray-100 border-gray-300 text-gray-900' : 'bg-gray-50 border-gray-300 text-gray-600 hover:text-gray-900'}`}
            >
              All Scholarships
            </button>
            <button
              data-testid="tab-matching"
              onClick={() => setViewMode('matching')}
              className={`flex-1 text-xs font-semibold py-2 rounded-lg border transition-colors flex items-center justify-center gap-1.5 ${viewMode === 'matching' ? 'bg-amber-600 border-amber-500 text-white' : 'bg-gray-50 border-gray-300 text-amber-700 hover:border-amber-500'}`}
            >
              <Star className="w-3 h-3" />
              Matching Scholarships
            </button>
          </div>
        )}

        {/* Filters */}
        <div className="mb-4">
          {/* Mobile toggle — always shown below md */}
          <button
            data-testid="toggle-filters-btn"
            className="md:hidden flex items-center gap-2 text-xs text-amber-700 font-medium mb-3"
            onClick={() => setFiltersOpen(v => !v)}
          >
            <Filter className="w-3.5 h-3.5" />
            {filtersOpen ? 'Hide Filters' : 'Show Filters'}
            {hasActiveFilters && (
              <span className="bg-amber-500 text-white text-[9px] rounded-full px-1.5 py-0.5">Active</span>
            )}
          </button>

          <div className={`${filtersOpen ? 'flex' : 'hidden'} md:flex flex-col gap-2`}>
            {/* Row 1: Country + Subject side-by-side */}
            <div className="flex gap-2">
              <select
                data-testid="country-filter"
                value={country}
                onChange={e => setCountry(e.target.value)}
                className="flex-1 bg-gray-50 border border-gray-300 text-gray-900 text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-amber-500"
              >
                {COUNTRIES.map(c => (
                  <option key={c} value={c === 'Worldwide' ? '' : c}>{c}</option>
                ))}
              </select>
              <select
                data-testid="subject-filter"
                value={subject}
                onChange={e => setSubject(e.target.value)}
                className="flex-1 bg-gray-50 border border-gray-300 text-gray-900 text-xs rounded-lg px-3 py-2 focus:outline-none focus:border-amber-500"
              >
                {SUBJECTS.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              {hasActiveFilters && (
                <button
                  onClick={handleFilterReset}
                  className="flex items-center gap-1 text-[11px] text-gray-600 hover:text-gray-900 transition-colors px-2 shrink-0"
                >
                  <X className="w-3 h-3" /> Reset
                </button>
              )}
            </div>

            {/* Row 2: Level pills — horizontal scroll, no wrap */}
            <div className="flex overflow-x-auto gap-1.5 pb-1 scrollbar-hide">
              {LEVELS.map(l => (
                <button
                  key={l.value}
                  data-testid={`level-filter-${l.value || 'all'}`}
                  onClick={() => setLevel(l.value)}
                  className={`text-[11px] font-medium px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap shrink-0 ${
                    level === l.value
                      ? 'bg-amber-600 border-amber-500 text-white'
                      : 'bg-gray-50 border-gray-300 text-gray-700 hover:border-amber-500 hover:text-amber-700'
                  }`}
                >
                  {l.label}
                </button>
              ))}
            </div>

            {/* Row 3: Type pills — horizontal scroll, no wrap */}
            <div className="flex overflow-x-auto gap-1.5 pb-1 scrollbar-hide">
              {TYPES.map(t => (
                <button
                  key={t.value}
                  data-testid={`type-filter-${t.value || 'all'}`}
                  onClick={() => setTypeFilter(t.value)}
                  className={`text-[11px] font-medium px-3 py-1.5 rounded-full border transition-colors whitespace-nowrap shrink-0 ${
                    typeFilter === t.value
                      ? 'bg-amber-600 border-amber-500 text-white'
                      : 'bg-gray-50 border-gray-300 text-gray-700 hover:border-amber-500 hover:text-amber-700'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Result summary */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-gray-600 text-xs">
            {loading ? 'Searching...' : `${total.toLocaleString()} scholarships`}
            {viewMode === 'matching' && !loading && <span className="text-amber-700 ml-1">· sorted by match</span>}
            {viewMode === 'all' && hasCv && !loading && <span className="text-amber-700 ml-1">· starred = CV match</span>}
          </p>
          {!loading && totalPages > 1 && (
            <p className="text-gray-500 text-[11px]">Page {page}/{totalPages}</p>
          )}
        </div>

        {/* Cards grid — 1 col mobile, 2 col tablet+ */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Loader2 className="w-7 h-7 text-amber-500 animate-spin" />
            <p className="text-gray-600 text-sm">Finding scholarships...</p>
          </div>
        ) : scholarships.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
            <Search className="w-10 h-10 text-gray-700" />
            <p className="text-gray-700 font-semibold text-sm">No scholarships found</p>
            <p className="text-gray-500 text-xs max-w-xs">
              {hasActiveFilters ? 'Try adjusting your filters.' : 'Our AI is fetching scholarships. Check back shortly.'}
            </p>
            {hasActiveFilters && (
              <button onClick={handleFilterReset} className="mt-1 text-amber-700 text-xs hover:underline">Reset filters</button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
            {scholarships.map((s, i) => (
              <ScholarshipCard key={`${s.title}-${i}`} scholarship={s} onViewDetails={handleViewDetails} />
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pb-8" data-testid="pagination">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="w-8 h-8 rounded-lg flex items-center justify-center bg-gray-50 border border-gray-300 text-gray-700 hover:border-amber-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronLeft className="w-4 h-4" />
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let p;
              if (totalPages <= 5) p = i + 1;
              else if (page <= 3) p = i + 1;
              else if (page >= totalPages - 2) p = totalPages - 4 + i;
              else p = page - 2 + i;
              return (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-8 h-8 rounded-lg text-xs font-medium border transition-colors ${page === p ? 'bg-amber-600 border-amber-500 text-white' : 'bg-gray-50 border-gray-300 text-gray-700 hover:border-amber-500'}`}>
                  {p}
                </button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="w-8 h-8 rounded-lg flex items-center justify-center bg-gray-50 border border-gray-300 text-gray-700 hover:border-amber-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Auth nudge */}
        {!user && scholarships.length > 0 && (
          <div className="border border-amber-800/50 bg-amber-100 rounded-xl p-4 flex items-start gap-3 mb-8">
            <AlertCircle className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-amber-700 font-medium text-sm">Sign in to view full details & apply</p>
              <p className="text-gray-600 text-xs mt-0.5">Access eligibility, application steps, and official university links.</p>
            </div>
            <button onClick={() => window.dispatchEvent(new CustomEvent('openAuthModal'))}
              className="flex-shrink-0 bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors">
              Sign In
            </button>
          </div>
        )}
      </div>

      <AdvisorRibbon />
      <Footer />

      {selectedScholarship && (
        <ScholarshipDetailModal scholarship={selectedScholarship} onClose={() => setSelectedScholarship(null)} />
      )}
    </div>
  );
}
